(ns app.front.view.group.admin.verdict-template.edit
  (:require [app.domain.verdict.model :as verdict-model]
            [app.domain.verdict.section :as verdict-section]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.admin.verdict-template.events :as vte]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-event-fx ::create-default
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/create-default-verdict-template
                    :data       (util/mmap group-id)
                    :notify     {:success :verdict/template-created}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::create-for-permit-type
  (fn [_ [_ group-id target-permit-type]]
    {:comm/command {:command    :group/create-superdomain-verdict-template
                    :data       (cond-> (util/mmap group-id)
                                  target-permit-type
                                  (assoc :target-permit-type target-permit-type))
                    :notify     {:success :verdict/template-created}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::delete
  (fn [_ [_ group-id template-id]]
    {:comm/command {:command    :group/delete-verdict-template
                    :data       (util/mmap group-id template-id)
                    :notify     {:success :verdict/template-deleted}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::copy
  (fn [_ [_ group-id template-id]]
    {:comm/command {:command    :group/copy-verdict-template
                    :data       (util/mmap group-id template-id)
                    :notify     {:success :verdict/template-copied}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::rename
  (fn [_ [_ group-id template-id new-name]]
    {:comm/command {:command    :group/modify-verdict-template
                    :data       (util/mmap {:data {:verdict.template/name new-name}}
                                           group-id template-id)
                    :notify     {:success :verdict/template-renamed}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::set-inherited-available
  (fn [_ [_ group-id template-id enabled?]]
    {:comm/command {:command    :group/set-template-availability
                    :data       (util/mmap {:domain/state (if enabled?
                                                            :verdict-template.state/enabled
                                                            :verdict-template.state/disabled)}
                                           template-id
                                           group-id)
                    :notify     {:success (if enabled? :verdict/template-enabled :verdict/template-disabled)}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::set-enabled
  (fn [_ [_ group-id template-id enabled?]]
    {:comm/command {:command    :group/modify-verdict-template
                    :data       (util/mmap {:data {:domain/state (if enabled?
                                                                   :verdict-template.state/enabled
                                                                   :verdict-template.state/disabled)}}
                                           group-id template-id)
                    :notify     {:success (if enabled? :verdict/template-enabled :verdict/template-disabled)}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::reset-section-css
  (fn [_ [_ group-id section-id]]
    {:comm/command {:command    :group/verdict-section-restore-default-css
                    :data       (util/mmap group-id section-id)
                    :notify     {:success :verdict/template-saved}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-db ::set-draft-section
  (fn [db [_ parent-id k v]]
    (assoc-in db [::adding parent-id k] v)))

(rf/reg-sub ::draft-section
  (fn [db [_ parent-id]]
    (get-in db [::adding parent-id])))

(rf/reg-sub ::require-identifier?
  (fn [[_ parent-id]]
    (rf/subscribe [::draft-section parent-id]))
  (fn [data [_ parent-id]]
    (and (not= parent-id
               (:verdict.section/template data))
         (-> data
             :verdict.section/type
             verdict-section/sub-types-without-mandatory-identifier
             not))))

(defn get-top-level-section [{:keys [:entity/parent :verdict.section/template]
                              :as   section}]
  (if (= parent template)
    section
    (get-top-level-section parent)))

(defn get-top-level-template [{:entity/keys [parent type]
                               :as          section}]
  (if (= type :entity.type/verdict-template)
    section
    (get-top-level-template parent)))

(rf/reg-sub ::top-level-section
  (fn [[_ template-id]]
    (rf/subscribe [::entities/entity template-id]))
  :-> get-top-level-section)

(rf/reg-sub ::siblings
  (fn [[_ parent-id]]
    (rf/subscribe [::top-level-section parent-id]))
  :-> eu/descendants)

(rf/reg-sub ::template-children
  (fn [[_ parent-id]]
    (rf/subscribe [::entities/entity parent-id]))
  (fn [parent _]
    (->> parent
         get-top-level-template
         eu/descendants)))

(rf/reg-sub ::used-identifiers
  (fn [[_ parent-id]]
    (rf/subscribe [::siblings parent-id]))
  (fn [sections _]
    (->> sections
         (keep :verdict.section/id)
         set)))

(rf/reg-sub ::used-on-any-template-page
  (fn [[_ parent-id]]
    (rf/subscribe [::template-children parent-id]))
  (fn [sections _]
    (->> sections
         (filter :verdict.section/id))))

(rf/reg-sub ::valid-draft?
  (fn [[_ parent-id]]
    [(rf/subscribe [::draft-section parent-id])
     (rf/subscribe [::require-identifier? parent-id])
     (rf/subscribe [::used-identifiers parent-id])
     (rf/subscribe [::used-on-any-template-page parent-id])])
  (fn [[{:verdict.section/keys [type id label]} require-identifier? used-ids used-on-any] _]
    (let [id       (or (verdict-section/get-static-id type) id)
          previous (->> used-on-any
                        (filter #(= (:verdict.section/id %) id))
                        first)]
      (and (or (not require-identifier?)
               ;; section under a HTML template needs a unique ID
               (and (some? id)
                    (-> id used-ids not)))
           (some? type)
           ;; static page requires label
           (or (not= type :verdict.section.type/static-page)
               (not (str/blank? label)))
           ;; don't allow same id with different type
           (or (not previous)
               (= (:verdict.section/type previous) type))))))

(rf/reg-sub ::draft-section-val
  (fn [[_ parent-id]]
    (rf/subscribe [::draft-section parent-id]))
  (fn [draft-section [_ _ k]]
    (get draft-section k)))

(rf/reg-event-fx ::add-section
  (fn [_ [_ group-id parent-id section-to-add]]
    {:comm/command {:command    :group/add-verdict-template-section
                    :data       (util/mmap group-id parent-id section-to-add)
                    :notify     {:success :verdict/section-added}
                    :on-success [::add-section.success parent-id]}}))

(rf/reg-event-fx ::add-section.success
  (fn [_ [_ parent-id status resp]]
    {:dispatch-n [[::cancel-section-add parent-id]
                  [::entities/apply-changes status resp]]}))

(rf/reg-event-db ::cancel-section-add
  (fn [db [_ parent-id]]
    (-> db
        (assoc-in [::adding parent-id] nil))))

(rf/reg-sub ::exclude-section-fn
  :<- [::gas/group]
  (fn [group _]
    (fn [section]
      (let [ctx (util/mmap group)]
        (or (verdict-model/exclude-section-by-type ctx section)
            (verdict-model/exclude-section-by-label ctx section))))))

(rf/reg-sub ::sections
  (fn [[_ section-or-template-id]]
    [(rf/subscribe [::exclude-section-fn])
     (rf/subscribe [::entities/entities {:entity/parent section-or-template-id
                                         :entity/type   :entity.type/verdict-section}])])
  (fn [[exclude-section-fn sections] _]
    (->> sections
         (sort-by :ordered/order)
         (remove exclude-section-fn))))

(rf/reg-sub ::template-used-in-express-issuance?
  (fn [[_ template-or-section-id]]
    (rf/subscribe [::entities/entity template-or-section-id]))
  (fn [template-or-section _]
    (->> template-or-section
         get-top-level-template
         verdict-model/template-used-in-express-issuance?)))

(rf/reg-sub ::addable-types
  (fn [[_ template-id]]
    [(rf/subscribe [:tr])
     (rf/subscribe [::siblings template-id])
     (rf/subscribe [::top-level-section template-id])
     (rf/subscribe [::exclude-section-fn])
     (rf/subscribe [::template-used-in-express-issuance? template-id])])
  (fn [[tr siblings top-level exclude-section-fn used-in-express?] [_ _ all-types]]
    (let [current-types    (->> siblings
                                (map :verdict.section/type)
                                set)
          disallowed-types (verdict-section/disallowed-types current-types
                                                             (:verdict.section/type top-level))]
      (->> (set/difference all-types
                           disallowed-types)
           (sort-by (comp tr vector))
           (remove #(exclude-section-fn {:verdict.section/type %}))
           (remove #(and used-in-express?
                         (contains? verdict-section/input-types-blocking-express-issuance %)))))))

(rf/reg-sub ::adding-section
  (fn [db [_ parent-id]]
    (-> db
        (get-in [::adding parent-id])
        boolean)))

(rf/reg-event-db ::start-add
  (fn [db [_ parent-id template-id]]
    (assoc-in db [::adding parent-id] {:verdict.section/template template-id})))

(defn text-input [{:keys [attr parent-id parse required]}]
  (let [tr    (<sub [:tr])
        parse (or parse identity)]
    [ui/text-input {:label-text (tr [attr])
                    :required   required
                    :value      (<sub [::draft-section-val parent-id attr])
                    :on-change  #(rf/dispatch-sync [::set-draft-section
                                                    parent-id
                                                    attr
                                                    (-> % futil/tval parse)])}]))

(defn remove-extras [s]
  (-> s
      str/lower-case
      (str/replace #"[^a-z0-9\\-]*" "")))

(def parse-section-id
  (comp keyword remove-extras))

(def subsection-container
  :div.border-2.border-ui-night-sky-50.border-solid.w-full.my-2.p-4)

(defn add-section-form [{:keys [parent-id types label]}]
  (let [tr   (<sub [:tr])
        type (<sub [::draft-section-val parent-id :verdict.section/type])]
    [subsection-container
     {:class "bg-ui-blue-10"}
     [:h5 (or label (tr [:verdict/add-section]))]
     [:div
      [:div.flex.mb-2.justify-between
       [:div
        [ui-select/select {:id           "add-new-section-select"
                           :grouper      (fn [type]
                                           (when type
                                             (cond
                                               (verdict-section/input-types type)
                                               (tr [:verdict.section/user-input])

                                               (verdict-section/top-level-types type)
                                               nil

                                               :else
                                               (tr [:verdict.section/static]))))
                           :label-text   (tr [:verdict.section/type])
                           :required     true
                           :allow-empty? false
                           :values       (<sub [::addable-types parent-id types])
                           :val->str     (comp tr vector)
                           :on-change    #(>evt [::set-draft-section parent-id :verdict.section/type %])
                           :value        type}]]
       (when-not (verdict-section/static-sub-types type)
         [:div.flex-1.ml-4
          [text-input (util/mmap {:attr     :verdict.section/label
                                  :required (= :verdict.section.type/static-page type)
                                  :class    "mr-4"}
                                 parent-id)]])]
      (when type
        [:div.flex.items-end.justify-between.max-w-2xl
         [:div.flex-1
          (if-let [static-id (verdict-section/get-static-id type)]
            [:div.mt-2
             [:h6.text-xs (tr [:verdict.section/id])]
             [:div static-id]]
            (when-not (verdict-section/top-level-types type)
              [text-input (util/mmap {:attr     :verdict.section/id
                                      :required (<sub [::require-identifier? parent-id])
                                      :parse    parse-section-id}
                                     parent-id)]))]
         (when (verdict-section/editable-sub-types type)
           (cc/checkbox {:id         (str parent-id "-required")
                         :class-name "ml-4"
                         :label-text (tr [:verdict.section/required?])
                         :on-change  #(>evt [::set-draft-section parent-id :verdict.section/required? %])
                         :checked    (boolean (<sub [::draft-section-val parent-id :verdict.section/required?]))}))])
      [:div.flex.justify-end.mt-4
       (cc/button {:on-click #(>evt [::cancel-section-add parent-id])
                   :kind     :tertiary}
                  (tr [:cancel]))
       (cc/button {:icon     i/plus
                   :disabled (not (<sub [::valid-draft? parent-id]))
                   :on-click #(>evt [::add-section
                                     (<sub [::gas/group-id])
                                     parent-id
                                     (<sub [::draft-section parent-id])])}
                  (tr [:add]))]]]))

(defn add-section [{:keys [parent-id template-id label] :as props}]
  (let [;; on top-level, the template is the parent
        parent-id (or parent-id template-id)]
    (if (<sub [::adding-section parent-id])
      [add-section-form (merge props (util/mmap parent-id label))]
      (cc/button {:on-click #(>evt [::start-add parent-id template-id])
                  :icon     i/plus}
                 label))))

(defn add-top-level-section [{:keys [template-id]}]
  (let [tr (<sub [:tr])]
    [add-section (util/mmap {:types verdict-section/top-level-types
                             :label (tr [:verdict/add-page])}
                            template-id)]))


(defn css-editor [{{:verdict.section/keys [print-css]
                    :db/keys              [id]}       :section}]
  (r/with-let [*css (r/atom nil)]
    (let [tr       (<sub [:tr])
          group-id (<sub [::gas/group-id])
          input-id (str "css-" id)]
      (if @*css
        [:div
         [:div.flex.justify-between
          [:label.bx--label
           {:for input-id}
           (tr [:verdict/edit-css])]
          [ui/modal-confirmation-button
           {:tr                  tr
            :text-button?        true
            :trigger-button-opts {:class-name "ml-4"}
            :trigger-button-text (tr [:verdict/reset-css])
            :on-yes-click        (fn [_]
                                   (reset! *css nil)
                                   (>evt [::reset-section-css
                                          group-id
                                          id]))}
           [:div
            (tr [:verdict/reset-css-help])]]]
         [ui/text-area
          {:id        input-id
           :value     @*css
           :on-change #(->> % futil/tval (reset! *css))}]
         [:div.flex.mt-2.justify-end
          (cc/button {:kind     :tertiary
                      :on-click #(reset! *css nil)}
                     (tr [:cancel]))
          (cc/button {:on-click (fn [_]
                                  (>evt [::vte/update-section
                                         group-id
                                         id
                                         {:verdict.section/print-css @*css}])
                                  (reset! *css nil))}
                     (tr [:save]))]]
        (cc/link {:on-click #(reset! *css (or print-css ""))
                  :href     "#"}
                 (tr [:verdict/edit-css]))))))

(defn css-view [{{:verdict.section/keys [print-css]
                  :db/keys              [id]}       :section}]
  (when-not (str/blank? print-css)
    (r/with-let [*css (r/atom nil)]
      (let [tr       (<sub [:tr])
            input-id (str "css-" id)]
        (if @*css
          [:div
           [ui/text-area
            {:id        input-id
             :read-only true
             :value     @*css
             :on-change #(->> % futil/tval (reset! *css))}]]
          (cc/link {:on-click #(reset! *css (or print-css ""))
                    :href     "#"}
                   (tr [:verdict/view-css])))))))
